import { filterFlatCatalog } from './utils';
import { PorchSelection } from './types';

// ============================================================================
// Placed Components Filters
// ============================================================================

/**
 * Builds the set of `uuid`s for every component currently placed in the shed. Returns null when
 * there are no components to read (so component-catalog filtering is skipped, preserving the
 * original behaviour of leaving the catalog untouched).
 *
 * @param components - Flat map of placed components keyed by instance key, from `getAllComponents()`.
 */
export function getPlacedComponentUuids(components: any): Set<string> | null {
	if (!components) {
		return null;
	}
	return new Set(Object.values(components).map((comp: any) => String(comp?.uuid)));
}

/**
 * Filters a component catalog down to entries that are actually placed in the current shed.
 * The catalog is an array of groups (e.g. `{ key: 'door', data: [...] }`, from
 * `window.model.componentCatalog`/`iComponentCatalog`). A catalog item matches a placed
 * component when its `id` or `ssb_configurator_component_name` is present in `placedUuids`
 * (which field applies depends on the component type). Group structure is preserved, only each
 * group's `data` is filtered.
 *
 * @param placedUuids - Set of placed component uuids from {@link getPlacedComponentUuids}, or null to skip filtering.
 * @param catalog - Catalog groups, from `window.model.componentCatalog`/`iComponentCatalog`.
 * @returns The catalog with each group's `data` filtered to entries present in `placedUuids`.
 */
export function filterComponentsByCatalog(placedUuids: Set<string> | null, catalog: any): any {
	if (!placedUuids || !Array.isArray(catalog)) {
		return catalog;
	}

	return catalog.map((group: any) => {
		if (!Array.isArray(group?.data)) {
			return group;
		}

		return {
			...group,
			data: group.data.filter(
				(item: any) =>
					placedUuids.has(String(item?.id)) ||
					placedUuids.has(String(item?.ssb_configurator_component_name)),
			),
		};
	});
}

// ============================================================================
// Upgrades & Packages Filters
// ============================================================================

/**
 * Reads the `data-unique` of every checked "other" upgrade input into a lookup set.
 */
export function getSelectedUpgradeUniques(): Set<string> {
	const uniques = new Set<string>();
	jQuery('[data-option-key="ssb-option-upgrade-other"]:checked').each((_i, el) => {
		const unique = jQuery(el).attr('data-unique');
		if (unique !== undefined && unique !== null && unique !== '') {
			uniques.add(String(unique));
		}
	});
	return uniques;
}

/**
 * Filters the "other" upgrades catalog down to what the snapshot should store, based on the
 * user's current selection. The catalog is an array of groups (from
 * `window.model.upgradesOtherCatalog`), each with a `data` array of upgrade items. Group
 * structure is preserved; only each group's `data` is filtered, and empty groups are dropped.
 *
 * Items are matched on their globally-unique `unique` id, rendered onto the selected inputs as
 * `data-unique`. The set of checked uniques is read from the DOM once and reused for every item.
 *
 * @param catalog - Upgrade groups, from `window.model.upgradesOtherCatalog`.
 */
export function filterUpgradesBySelection(catalog: any): any {
	if (!Array.isArray(catalog)) {
		return catalog;
	}

	const selectedUniques = getSelectedUpgradeUniques();

	return catalog
		.map((group: any) => {
			if (!Array.isArray(group?.data)) {
				return group;
			}

			return {
				...group,
				data: group.data.filter((item: any) => {
					const unique = item?.unique;
					if (unique === undefined || unique === null || String(unique) === '') {
						return false;
					}
					return selectedUniques.has(String(unique));
				}),
			};
		})
		.filter((group: any) => Array.isArray(group?.data) && group.data.length > 0);
}

/**
 * Reads the (base64) `data-custom-key` of every checked package input into a lookup set.
 */
export function getSelectedPackageKeys(): Set<string> {
	const keys = new Set<string>();
	jQuery('[data-option-key="ssb-option-upgrade-package"]:checked').each((_i, el) => {
		// const key = jQuery(el).attr('data-custom-key');
		const key = jQuery(el).attr('data-unique');
		if (key !== undefined && key !== null && key !== '') {
			keys.add(key);
		}
	});
	return keys;
}

/**
 * Filters the "package" upgrades catalog (a flat array from `window.model.upgradesPackageCatalog`)
 * down to the currently-selected items. Package inputs have no `data-unique`, so items are matched
 * on their `ssb_custom_key`, rendered onto the input as `data-custom-key` (base64 of the key). The
 * set of checked keys is read from the DOM once and reused for every item.
 *
 * @param catalog - Package upgrade items, from `window.model.upgradesPackageCatalog`.
 */
export function filterPackageBySelection(catalog: any): any {
	if (!Array.isArray(catalog)) {
		return catalog;
	}

	const selectedKeys = getSelectedPackageKeys();
	return catalog
		.map((group: any) => {
			if (!Array.isArray(group?.data)) {
				return group;
			}

			return {
				...group,
				data: group.data.filter((item: any) => {
					const unique = item?.unique;
					if (unique === undefined || unique === null || String(unique) === '') {
						return false;
					}
					return selectedKeys.has(String(unique));
				}),
			};
		})
		.filter((group: any) => Array.isArray(group?.data) && group.data.length > 0);
}

// ============================================================================
// Single-Select Upgrade Catalogs
// ============================================================================

/**
 * Reads the checked loft input's `origin-value`, or null when nothing is selected.
 */
export function getSelectedLoftName(): string | null {
	const checked = jQuery('[data-option-key="ssb-option-upgrade-loft"]:checked');
	if (!checked.length) {
		return null;
	}
	return String(checked.attr('origin-value') ?? '');
}

/**
 * Filters the loft catalog (a single-select radio group from `window.model.loftCatalog`) down to
 * the selected item. Loft inputs carry the raw item name in `origin-value`, matched against the
 * catalog item's `name`. The checked value is read from the DOM once.
 *
 * @param catalog - Loft items, from `window.model.loftCatalog`.
 */
export function filterLoftBySelection(catalog: any): any {
	const selectedName = getSelectedLoftName();
	return filterFlatCatalog(catalog, (item: any) => {
		const name = item?.name;
		if (name === undefined || name === null || String(name) === '') {
			return false;
		}
		return selectedName !== null && String(name) === selectedName;
	});
}

/**
 * Reads the checked runway input's `data-key`, or null when nothing is selected.
 */
export function getSelectedRunwayId(): string | null {
	const checked = jQuery('input[name="ssb-configurator-upgrade-runway"]:checked');
	if (!checked.length) {
		return null;
	}
	return String(checked.attr('data-key') ?? '');
}

/**
 * Filters the runway-options catalog (a single-select radio group from
 * `window.model.runwayOptionsCatalog`) down to the selected item. Runway inputs carry the item's
 * `id` in `data-key`. The checked value is read from the DOM once.
 *
 * @param catalog - Runway items, from `window.model.runwayOptionsCatalog`.
 */
export function filterRunwayBySelection(catalog: any): any {
	const selectedId = getSelectedRunwayId();
	return filterFlatCatalog(catalog, (item: any) => {
		const id = item?.id;
		if (id === undefined || id === null || String(id) === '') {
			return false;
		}
		return selectedId !== null && String(id) === selectedId;
	});
}

/**
 * Reads the checked corner-trim input's `data-key`, or null when nothing is selected.
 */
export function getSelectedCornerTrimId(): string | null {
	const checked = jQuery('input[name="ssb-configurator-upgrade-corner-trim"]:checked');
	if (!checked.length) {
		return null;
	}
	return String(checked.attr('data-key') ?? '');
}

/**
 * Filters the corner-trim catalog (a single-select radio group from
 * `window.model.cornerTrimCatalog`) down to the selected item. Corner-trim inputs carry the
 * item's `id` in `data-key`. The checked value is read from the DOM once.
 *
 * @param catalog - Corner-trim items, from `window.model.cornerTrimCatalog`.
 */
export function filterCornerTrimBySelection(catalog: any): any {
	const selectedId = getSelectedCornerTrimId();
	return filterFlatCatalog(catalog, (item: any) => {
		const id = item?.id;
		if (id === undefined || id === null || String(id) === '') {
			return false;
		}
		return selectedId !== null && String(id) === selectedId;
	});
}

/**
 * Reads the checked electrical-package input's `data-option-original-value`, or null when nothing is selected.
 */
export function getSelectedElectricalPackageName(): string | null {
	const checked = jQuery('input[name="ssb-configurator-electrical-packages"]:checked');
	if (!checked.length) {
		return null;
	}
	const originalValue = checked.attr('data-option-original-value');

	if (originalValue === undefined || originalValue === null || originalValue === '') {
		return null;
	}
	return String(originalValue);
}

/**
 * Filters the electrical-packages catalog (a single-select radio group from
 * `window.model.electricalPackages`) down to the selected item. Package inputs carry the item's
 * original name in `data-option-original-value`, matched against the catalog item's
 * `ssb_configurator_package_name`. The checked value is read from the DOM once. The "None" radio
 * has no `data-option-original-value`, so it resolves to null and yields an empty catalog.
 *
 * @param catalog - Electrical-package items, from `window.model.electricalPackages`.
 */
export function filterElectricalPackageBySelection(catalog: any): any {
	const selectedName = getSelectedElectricalPackageName();
	return filterFlatCatalog(catalog, (item: any) => {
		const name = item?.ssb_configurator_package_name;
		if (name === undefined || name === null || String(name) === '') {
			return false;
		}
		return selectedName !== null && String(name) === selectedName;
	});
}

/**
 * Reads the checked tackroom input's `data-option-value`, or null when nothing is selected.
 */
export function getSelectedTackroomName(): string | null {
	const checked = jQuery('input[name="ssb-configurator-upgrade-tackroom"]:checked');
	if (!checked.length) {
		return null;
	}
	const optionValue = checked.attr('data-option-value');
	if (optionValue === undefined || optionValue === null || optionValue === '') {
		return null;
	}
	return String(optionValue);
}

/**
 * Filters the tackroom catalog (a single-select radio group from `window.model.tackroomItems`)
 * down to the selected item. Tackroom inputs carry the item's name in `data-option-value`,
 * matched against the catalog item's `custom_name`. The checked value is read from the DOM once;
 * when nothing is selected it resolves to null and yields an empty catalog.
 *
 * @param catalog - Tackroom items, from `window.model.tackroomItems`.
 */
export function filterTackroomBySelection(catalog: any): any {
	const selectedName = getSelectedTackroomName();
	return filterFlatCatalog(catalog, (item: any) => {
		const name = item?.custom_name;
		if (name === undefined || name === null || String(name) === '') {
			return false;
		}
		return selectedName !== null && String(name) === selectedName;
	});
}

/**
 * Filters the frame catalog down to the selected item based on `data-option-value-base64`.
 *
 * @param catalog - Frame items, from `window.model.frameCatalog`.
 */
export function filterFrameCatalog(catalog: any): any {
	const selectedValue = jQuery('[name="ssb-configurator-upgrade-frame"]:checked').data(
		'option-value-base64',
	);
	if (selectedValue === undefined || selectedValue === null || String(selectedValue) === '') {
		return filterFlatCatalog(catalog, () => false);
	}

	return filterFlatCatalog(catalog, (item: any) => {
		const itemValue = String(btoa(item?.file + '-' + item?.name) ?? '');
		return itemValue === String(selectedValue);
	});
}

// ============================================================================
// Porch Catalog Filters
// ============================================================================

/**
 * Resolves the current porch selection from the DOM. Porch has two render paths:
 *
 * 1. A single radio group (`name="ssb-configurator-upgrade-porch"`) whose checked input carries
 *    the item's id in `data-porch-uuid` → `{ mode: 'direct' }`.
 * 2. Split depth + width radio groups (`ssb-configurator-upgrade-porch-depth` / `-porch-width`),
 *    where the checked depth's `data-porch-size` and width's `data-porch-width-value` identify the
 *    item → `{ mode: 'split' }`. `width` is null when no width radio is checked.
 *
 * When neither path has a selection the result is `{ mode: 'none' }`.
 */
export function getPorchSelection(): PorchSelection {
	const directChecked = jQuery('input[name="ssb-configurator-upgrade-porch"]:checked');
	if (directChecked.length) {
		return { mode: 'direct', uuid: String(directChecked.attr('data-porch-uuid') ?? '') };
	}

	const depthChecked = jQuery('input[name="ssb-configurator-upgrade-porch-depth"]:checked');
	if (!depthChecked.length) {
		return { mode: 'none' };
	}

	const depth = String(depthChecked.attr('data-porch-size') ?? '');
	const widthChecked = jQuery('input[name="ssb-configurator-upgrade-porch-width"]:checked');
	const width = widthChecked.length
		? String(widthChecked.attr('data-porch-width-value') ?? '')
		: null;

	return { mode: 'split', depth, width };
}

/**
 * Decides whether a porch item matches the resolved {@link PorchSelection}. Width defaults to 8
 * when the item leaves it unset (mirroring the PHP render).
 *
 * @param item - A single porch item from `porchCatalog`.
 * @param selection - The porch selection resolved once from the DOM.
 */
export function isPorchItemSelected(item: any, selection: PorchSelection): boolean {
	if (selection.mode === 'none') {
		return false;
	}

	if (selection.mode === 'direct') {
		const uuid = item?.ssb_configurator_upgrade_porch_id;
		if (uuid === undefined || uuid === null || String(uuid) === '') {
			return false;
		}
		return String(uuid) === selection.uuid;
	}

	// Split depth + width path.
	if (
		selection.depth === '' ||
		String(item?.ssb_configurator_upgrade_porch_size ?? '') !== selection.depth
	) {
		return false;
	}

	if (selection.width === null) {
		return true;
	}

	const itemWidth = String(item?.ssb_configurator_upgrade_porch_width_value || 8);
	return itemWidth === selection.width;
}

/**
 * Filters the porch catalog (single-select, from `window.model.porchCatalog`) down to the selected
 * item. The porch selection is resolved once from the DOM (see {@link getPorchSelection}) and then
 * compared against each catalog item.
 *
 * @param catalog - Porch items, from `window.model.porchCatalog`.
 */
export function filterPorchBySelection(catalog: any): any {
	const selection = getPorchSelection();
	return filterFlatCatalog(catalog, (item: any) => isPorchItemSelected(item, selection));
}

// ============================================================================
// Material Filters
// ============================================================================

/**
 * Filters a material catalog down to the single material the user currently has selected.
 * Material options render as `<li>` thumbnails (not inputs); the selected one carries the
 * `active` class and exposes the raw material value in `data-material-type` — the same value
 * the builder reads elsewhere (e.g. `#insulation_wall-material-option li.active`). Each catalog
 * item's value lives at `ssb_configurator_material.value`, so the active `<li>`'s
 * `data-material-type` matches it directly. Non-array catalogs are returned unchanged; when
 * nothing is selected the catalog is emptied (single-select, like loft/runway/porch).
 *
 * Most sections carry the value in `data-material-type` and store it at
 * `ssb_configurator_material.value`; floor differs (`data-floor-type` / `material.value`), so the
 * attribute and the per-item value accessor are overridable.
 *
 * @param catalog - Material items, e.g. `window.model.insulationWallMaterials`.
 * @param optionId - DOM id of the material `<ul>` (e.g. `insulation_wall-material-option`).
 * @param selectedAttr - Attribute on the active `<li>` holding the selected value.
 * @param getItemValue - Reads the comparable value from a catalog item.
 */
export function filterMaterials(
	catalog: any,
	optionId: string,
	selectedAttr: string = 'data-material-type',
	getItemValue: (item: any) => any = (item: any) => item?.ssb_configurator_material?.value,
): any {
	const selectedValue = jQuery(`#${optionId} li.active`).attr(selectedAttr);
	if (selectedValue === undefined || selectedValue === null || String(selectedValue) === '') {
		return filterFlatCatalog(catalog, () => false);
	}

	return filterFlatCatalog(
		catalog,
		(item: any) => String(getItemValue(item) ?? '') === String(selectedValue),
	);
}
